import { endOfMinute, startOfMinute } from 'date-fns';

/**
 * Named calendar quick-periods offered by DateRangeSelectionComponent's
 * preset bar. Consumers may also resolve one programmatically via
 * quickPeriodRange() (e.g. a view seeding its default period), so the range
 * a name stands for is defined once, here.
 */
export const QUICK_PERIOD_VALUES = [
  '1Y',
  '6M',
  '3M',
  '1M',
  '2W',
  '1W',
  '3D',
  '24H',
  'today',
] as const;

export type QuickPeriodValue = (typeof QUICK_PERIOD_VALUES)[number];

export function isQuickPeriodValue(value: string): value is QuickPeriodValue {
  return (QUICK_PERIOD_VALUES as readonly string[]).includes(value);
}

/**
 * The date range a named preset stands for: calendar periods anchor to today
 * (local start-of-day -> end-of-day), '24H' alone is now-relative. Normalized
 * to the :00/:59 convention - seconds never ride into a range, since ranges
 * travel into the query-cache keys (the memcached layer in front of Solr).
 */
export function quickPeriodRange(period: QuickPeriodValue): {
  from: Date;
  to: Date;
} {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  // End of today
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  // Period-to-calendar logic
  const periodMap: Record<QuickPeriodValue, (start: Date) => Date> = {
    today: (start) => start,
    '24H': () => new Date(Date.now() - 24 * 60 * 60 * 1000),

    '3D': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 3);
      return d;
    },
    '1W': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 7);
      return d;
    },
    '2W': (start) => {
      let d = new Date(start);
      d.setDate(d.getDate() - 14);
      return d;
    },

    '1M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 1);
      return d;
    },
    '3M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 3);
      return d;
    },
    '6M': (start) => {
      let d = new Date(start);
      d.setMonth(d.getMonth() - 6);
      return d;
    },
    '1Y': (start) => {
      let d = new Date(start);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    },
  };

  const from = periodMap[period](todayStart);
  const to = period === '24H' ? new Date() : todayEnd;

  return { from: startOfMinute(from), to: endOfMinute(to) };
}

/** One option of the time preset row: a now-relative duration (Grafana convention, '5m' = five minutes). */
export interface TimePresetOption {
  label: string;
  value: string;
  minutes: number;
  /** i18n key of the tooltip that spells the preset out ("Last 5 minutes"). */
  tooltipKey: string;
}

/** The default time preset row, longest first. */
export const DEFAULT_TIME_PRESETS: TimePresetOption[] = [
  {
    label: '12h',
    value: '12h',
    minutes: 720,
    tooltipKey: 'date-range-selection.tooltip.last-12-hours',
  },
  {
    label: '8h',
    value: '8h',
    minutes: 480,
    tooltipKey: 'date-range-selection.tooltip.last-8-hours',
  },
  {
    label: '4h',
    value: '4h',
    minutes: 240,
    tooltipKey: 'date-range-selection.tooltip.last-4-hours',
  },
  {
    label: '1h',
    value: '1h',
    minutes: 60,
    tooltipKey: 'date-range-selection.tooltip.last-1-hour',
  },
  {
    label: '30m',
    value: '30m',
    minutes: 30,
    tooltipKey: 'date-range-selection.tooltip.last-30-minutes',
  },
  {
    label: '15m',
    value: '15m',
    minutes: 15,
    tooltipKey: 'date-range-selection.tooltip.last-15-minutes',
  },
  {
    label: '5m',
    value: '5m',
    minutes: 5,
    tooltipKey: 'date-range-selection.tooltip.last-5-minutes',
  },
];

/**
 * Minutes a now-relative preset spans ('5m' .. '12h' of the time row, '24H' of
 * the calendar row), `null` for a calendar preset ('today', '1W', ...) or none:
 * those anchor to calendar days and never slide.
 */
export function presetMinutes(
  preset: string | null | undefined,
): number | null {
  let result: number | null = null;
  if (preset === '24H') {
    result = 24 * 60;
  } else if (preset) {
    const match = /^(\d+)([mh])$/.exec(preset);
    if (match) {
      result = Number(match[1]) * (match[2] === 'h' ? 60 : 1);
    }
  }
  return result;
}

/** Tooltip keys of the calendar preset row, the meaning behind each terse label. */
export const QUICK_PERIOD_TOOLTIP_KEYS: Record<QuickPeriodValue, string> = {
  '1Y': 'date-range-selection.tooltip.last-1-year',
  '6M': 'date-range-selection.tooltip.last-6-months',
  '3M': 'date-range-selection.tooltip.last-3-months',
  '1M': 'date-range-selection.tooltip.last-1-month',
  '2W': 'date-range-selection.tooltip.last-2-weeks',
  '1W': 'date-range-selection.tooltip.last-1-week',
  '3D': 'date-range-selection.tooltip.last-3-days',
  '24H': 'date-range-selection.tooltip.last-24-hours',
  today: 'date-range-selection.tooltip.since-today-midnight',
};
